import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '@services/api';
import useCartStore from './cartStore';

const useAuthStore = create(
    persist(
        (set, get) => ({
            user: null,
            token: null,
            isAuthenticated: false,
            isLoading: false,
            error: null,

            // Set loading state
            setLoading: (loading) => set({ isLoading: loading }),

            // Login
            login: async (email, password) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await api.post('/auth/login', { email, password });
                    const { user, token } = response.data.data;

                    set({
                        user,
                        token,
                        isAuthenticated: true,
                        isLoading: false,
                        error: null,
                    });

                    // Set token in API headers
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

                    return { success: true, user };
                } catch (error) {
                    const message = error.response?.data?.message || 'Login failed';
                    set({ isLoading: false, error: message });
                    return { success: false, error: message };
                }
            },

            // Register
            register: async (userData) => {
                set({ isLoading: true, error: null });
                try {
                    await api.post('/auth/register', userData);
                    // Don't auto-login after register, wait for email verification
                    set({ isLoading: false });
                    return { success: true };
                } catch (error) {
                    const message = error.response?.data?.message || 'Registration failed';
                    set({ isLoading: false, error: message });
                    return { success: false, error: message };
                }
            },

            // Logout
            logout: async () => {
                try {
                    await api.post('/auth/logout');
                } catch (error) {
                    console.error('Logout error:', error);
                } finally {
                    set({
                        user: null,
                        token: null,
                        isAuthenticated: false,
                        error: null,
                    });
                    useCartStore.getState().clearCart();
                    delete api.defaults.headers.common['Authorization'];
                }
            },

            // Get current user
            getMe: async () => {
                const token = get().token;
                if (!token) return;

                set({ isLoading: true });
                try {
                    api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                    const response = await api.get('/auth/me');
                    set({
                        user: response.data.data.user,
                        isAuthenticated: true,
                        isLoading: false,
                    });
                } catch (error) {
                    set({
                        user: null,
                        token: null,
                        isAuthenticated: false,
                        isLoading: false,
                    });
                    delete api.defaults.headers.common['Authorization'];
                }
            },

            // Update user
            updateUser: (userData) => {
                set((state) => ({
                    user: { ...state.user, ...userData },
                }));
            },

            // Verify email
            verifyEmail: async (token) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await api.get(`/auth/verify-email/${token}`);
                    set({ isLoading: false });
                    return { success: true, message: response.data.message };
                } catch (error) {
                    const message = error.response?.data?.message || 'Email verification failed';
                    set({ isLoading: false, error: message });
                    return { success: false, error: message };
                }
            },

            // Forgot Password
            forgotPassword: async (email) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await api.post('/auth/forgot-password', { email });
                    set({ isLoading: false });
                    return { success: true, message: response.data.message };
                } catch (error) {
                    const message = error.response?.data?.message || 'Failed to send reset email';
                    set({ isLoading: false, error: message });
                    return { success: false, error: message };
                }
            },

            // Reset Password
            resetPassword: async (token, password) => {
                set({ isLoading: true, error: null });
                try {
                    const response = await api.post(`/auth/reset-password/${token}`, { password });
                    set({ isLoading: false });
                    return { success: true, message: response.data.message };
                } catch (error) {
                    const message = error.response?.data?.message || 'Password reset failed';
                    set({ isLoading: false, error: message });
                    return { success: false, error: message };
                }
            },

            // Clear error
            clearError: () => set({ error: null }),
        }),
        {
            name: 'voe-auth-storage',
            partialize: (state) => ({
                token: state.token,
                user: state.user,
                isAuthenticated: state.isAuthenticated
            }),
        }
    )
);

export default useAuthStore;
